'use client';

import { Button } from '@savvy-kit/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { Check, ChevronsUpDown } from 'lucide-react';

export type HouseholdSwitcherMembership = {
  readonly id: string;
  readonly name: string;
};

export type HouseholdSwitcherProps = {
  readonly activeHouseholdId: string | null;
  readonly memberHouseholds: readonly HouseholdSwitcherMembership[];
  readonly onSelect: (householdId: string) => void;
  readonly disabled?: boolean;
};

export function HouseholdSwitcher({ activeHouseholdId, memberHouseholds, onSelect, disabled }: HouseholdSwitcherProps) {
  if (memberHouseholds.length <= 1) {
    return null;
  }

  const active = memberHouseholds.find((h) => h.id === activeHouseholdId) ?? memberHouseholds[0];
  const activeName = active?.name ?? '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="secondary"
            disabled={disabled}
            data-testid="household-switcher-trigger"
            aria-label="Switch household"
            className="w-[350px] justify-between text-dark"
          />
        }
      >
        {activeName}
        <ChevronsUpDown className="size-4 text-secondary" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-[1200] w-[350px]">
        {memberHouseholds.map((household) => {
          const isActive = household.id === active?.id;
          return (
            <DropdownMenuItem
              key={household.id}
              onClick={() => {
                if (!isActive) {
                  onSelect(household.id);
                }
              }}
              data-testid={`household-switcher-option-${household.id}`}
            >
              <span className="flex-1">{household.name}</span>
              {isActive ? <Check className="size-4 text-secondary" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
